import { pointDistance, pointFrom } from "@excalidraw/math";

import type { GlobalPoint, LocalPoint } from "@excalidraw/math";

import { LinearElementEditor } from "../linearElementEditor";

import { getAbsHandles, segmentToCurve } from "./pathGeometry";

import type { ElementsMap } from "../types";

import type { PenPathElement, PenPointHandles } from "./types";

type HandleSide = "in" | "out";
type HandleHit = { anchor: number; side: HandleSide };

/** snaps a vector's angle to the nearest 45° multiple, preserving length */
const snapTo45 = (dx: number, dy: number): [number, number] => {
  const len = Math.hypot(dx, dy);
  if (len === 0) {
    return [dx, dy];
  }
  const step = Math.PI / 4;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  return [Math.cos(angle) * len, Math.sin(angle) * len];
};

/**
 * Selected anchors' visible handles as { anchor, side, absolute } where
 * `absolute` is the handle position in the element's local coordinate space
 * (anchor + offset). Used for rendering tangent lines + dots and hit-testing.
 */
export const getVisibleHandles = (
  element: PenPathElement,
  selectedIndices: readonly number[] | null,
): { anchor: number; side: HandleSide; absolute: LocalPoint }[] => {
  const result: { anchor: number; side: HandleSide; absolute: LocalPoint }[] =
    [];
  for (const anchor of new Set(selectedIndices ?? [])) {
    const abs = getAbsHandles(element, anchor);
    if (abs.in) {
      result.push({ anchor, side: "in", absolute: abs.in });
    }
    if (abs.out) {
      result.push({ anchor, side: "out", absolute: abs.out });
    }
  }
  return result;
};

/**
 * Hit-tests bezier handles of SELECTED anchors (Penpot: only selected anchors'
 * handles are interactive). Threshold is POINT_HANDLE_SIZE / zoomValue in scene
 * units; accounts for element rotation the same way the point hit-test does.
 */
export const hitTestHandle = (
  element: PenPathElement,
  selectedIndices: readonly number[] | null,
  scenePointer: GlobalPoint,
  zoomValue: number,
  elementsMap: ElementsMap,
): HandleHit | null => {
  let hit: HandleHit | null = null;
  let bestDist = LinearElementEditor.POINT_HANDLE_SIZE / zoomValue;
  for (const { anchor, side, absolute } of getVisibleHandles(
    element,
    selectedIndices,
  )) {
    const global = LinearElementEditor.getPointGlobalCoordinates(
      element,
      absolute,
      elementsMap,
    );
    const d = pointDistance(scenePointer, global);
    if (d <= bestDist) {
      bestDist = d;
      hit = { anchor, side };
    }
  }
  return hit;
};

/** cubic bezier point at t */
const bezierAt = (
  [p0, c1, c2, p1]: [LocalPoint, LocalPoint, LocalPoint, LocalPoint],
  t: number,
): LocalPoint => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return pointFrom<LocalPoint>(
    a * p0[0] + b * c1[0] + c * c2[0] + d * p1[0],
    a * p0[1] + b * c1[1] + c * c2[1] + d * p1[1],
  );
};

/**
 * Hit-tests the pen path's segments themselves (Figma-style "hover the line to
 * add a node"). Returns the closest on-curve point within `threshold` (scene
 * units) plus the segment index and bezier parameter t needed to split there.
 */
export const hitTestPenSegment = (
  element: PenPathElement,
  scenePointer: GlobalPoint,
  threshold: number,
  elementsMap: ElementsMap,
): { segmentIndex: number; t: number; point: GlobalPoint } | null => {
  const local = LinearElementEditor.createPointAt(
    element,
    elementsMap,
    scenePointer[0],
    scenePointer[1],
    null,
  );
  const STEPS = 20;
  let best: { segmentIndex: number; t: number } | null = null;
  let bestDist = threshold;
  for (let i = 0; i < element.points.length - 1; i++) {
    const curve = segmentToCurve(element.points, element.handles, i);
    for (let k = 0; k <= STEPS; k++) {
      const t = k / STEPS;
      const d = pointDistance(local, bezierAt(curve, t));
      if (d < bestDist) {
        bestDist = d;
        best = { segmentIndex: i, t };
      }
    }
  }
  if (!best) {
    return null;
  }
  // ternary-search refine around the best coarse sample
  const curve = segmentToCurve(
    element.points,
    element.handles,
    best.segmentIndex,
  );
  let lo = Math.max(0, best.t - 1 / STEPS);
  let hi = Math.min(1, best.t + 1 / STEPS);
  for (let iter = 0; iter < 20; iter++) {
    const m1 = lo + (hi - lo) / 3;
    const m2 = hi - (hi - lo) / 3;
    if (
      pointDistance(local, bezierAt(curve, m1)) <
      pointDistance(local, bezierAt(curve, m2))
    ) {
      hi = m2;
    } else {
      lo = m1;
    }
  }
  const t = (lo + hi) / 2;
  return {
    segmentIndex: best.segmentIndex,
    t,
    point: LinearElementEditor.getPointGlobalCoordinates(
      element,
      bezierAt(curve, t),
      elementsMap,
    ),
  };
};

/**
 * Drags one bezier handle. New offset = localPointer − anchor (45°-snapped with
 * shift). Without alt, mirrors the opposite side when it exists (opposite =
 * −offset); with alt, leaves the opposite side untouched (breaks symmetry
 * permanently, like Penpot). Returns the full next `handles` array.
 */
export const dragPenHandleUpdate = (
  element: PenPathElement,
  hit: HandleHit,
  localPointer: LocalPoint,
  modifiers: { alt: boolean; shift: boolean },
): { handles: PenPointHandles[] } => {
  const { anchor, side } = hit;
  const a = element.points[anchor];
  let dx = localPointer[0] - a[0];
  let dy = localPointer[1] - a[1];
  if (modifiers.shift) {
    [dx, dy] = snapTo45(dx, dy);
  }
  const opposite: HandleSide = side === "in" ? "out" : "in";
  const prev = element.handles[anchor];
  const next: { in?: LocalPoint; out?: LocalPoint } = { ...(prev ?? {}) };
  next[side] = pointFrom<LocalPoint>(dx, dy);
  if (!modifiers.alt && prev?.[opposite]) {
    next[opposite] = pointFrom<LocalPoint>(-dx, -dy);
  }
  const handles = element.handles.map((h, i) =>
    i === anchor ? next : h,
  ) as PenPointHandles[];
  return { handles };
};

/**
 * When node-snapping is enabled, snaps a dragged position to the nearest OTHER
 * anchor of the same element within POINT_HANDLE_SIZE / zoomValue (local units);
 * otherwise returns the input unchanged.
 */
export const snapToNodes = (
  element: PenPathElement,
  draggedIndices: number[],
  localPointer: LocalPoint,
  zoomValue: number,
): LocalPoint => {
  const threshold = LinearElementEditor.POINT_HANDLE_SIZE / zoomValue;
  const exclude = new Set(draggedIndices);
  let best: LocalPoint | null = null;
  let bestDist = threshold;
  element.points.forEach((pt, i) => {
    if (exclude.has(i)) {
      return;
    }
    const d = pointDistance(localPointer, pt);
    if (d <= bestDist) {
      bestDist = d;
      best = pt;
    }
  });
  return best ?? localPointer;
};
